import { Animated, FlatList, Pressable, RefreshControl, View, Image, ActivityIndicator, Platform, TextInput } from 'react-native';
import React, { useCallback, useState, useMemo } from 'react';
import DefaultScreen from '@components/Global/DefaultScreenView';
import apis from '@api/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import AchievementBackground from '@assets/--native/AchievementBackground';
import parseByDelimiter from '@utils/parseByDelimiter';
import moment from 'moment';
import MaskedView from '@react-native-masked-view/masked-view';
import { Text } from '@constants/Themed';
import { Entypo, Ionicons, AntDesign, FontAwesome, Feather, MaterialIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Dialog, XStack, YStack, SizableText } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

import { useDeviceType } from '@hooks/useDeviceType';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useSelector } from 'react-redux';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';

// Helper function to safely parse invoice template data
const parseInvoiceTemplate = (item) => {
  if (!item || typeof item !== 'object') {
    return {
      id: null,
      title: 'Untitled Invoice Template',
      invoice_template_name: 'Untitled Invoice Template',
      template_description: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: null,
      creatorName: 'Unknown',
      creatorAvatar: null,
      type: 'invoice',
    };
  }

  // Safely extract title/name
  const title = item.title || item.invoice_template_name || item.name || 'Untitled Invoice Template';
  const parsedTitle = parseByDelimiter(title) || title;

  // Safely extract description
  const description = item.template_description || item.description || '';

  // Safely extract dates
  const createdAt = item.createdAt || item.created_at || new Date().toISOString();
  const updatedAt = item.updatedAt || item.updated_at || createdAt;

  // Safely extract createdBy information
  let creatorName = 'Unknown';
  let creatorAvatar = null;

  if (item.createdBy) {
    if (typeof item.createdBy === 'object') {
      // Populated user object
      const profileInfo = item.createdBy?.profile_information || item.createdBy?.profileInformation || {};
      creatorName = profileInfo?.name || item.createdBy?.name || item.createdBy?.username || 'Unknown';
      const avatar = profileInfo?.avatar || item.createdBy?.avatar || {};
      creatorAvatar = avatar?.img || avatar?.image || avatar?.url || null;
    } else if (typeof item.createdBy === 'string') {
      // Just an ID
      creatorName = 'Unknown';
    }
  }

  return {
    id: item._id || item.id || null,
    title: parsedTitle,
    invoice_template_name: parsedTitle,
    template_description: description,
    createdAt,
    updatedAt,
    createdBy: item.createdBy,
    creatorName,
    creatorAvatar,
    type: item.type || 'invoice',
    ...item, // Spread original item to preserve any additional fields
  };
};

const InvoiceTemplates = ({ navigation }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeItem, setActiveItem] = useState({});
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isTablet } = useDeviceType();
  const theme = themeSelector();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat) || 'MM/DD/YYYY';
  const queryClient = useQueryClient();

  const { status, data: invoiceTemplatesRaw, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['invoiceTemplates'],
    queryFn: async () => {
      try {
        const data = await apis.getInvoiceTemplates();
        // Ensure we always return an array
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (data.data && Array.isArray(data.data)) return data.data;
        return [];
      } catch (err) {
        console.error('Error fetching invoice templates:', err);
        return [];
      }
    },
    retry: 2,
    staleTime: 30000, // 30 seconds
  });

  // Parse and normalize invoice templates
  const invoiceTemplates = useMemo(() => {
    if (!invoiceTemplatesRaw || !Array.isArray(invoiceTemplatesRaw)) {
      return [];
    }
    return invoiceTemplatesRaw.map(parseInvoiceTemplate);
  }, [invoiceTemplatesRaw]);

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) {
      return invoiceTemplates;
    }
    const query = searchQuery.toLowerCase().trim();
    return invoiceTemplates.filter(template => {
      const title = (template.title || '').toLowerCase();
      const description = (template.template_description || '').toLowerCase();
      const creatorName = (template.creatorName || '').toLowerCase();
      return title.includes(query) || description.includes(query) || creatorName.includes(query);
    });
  }, [invoiceTemplates, searchQuery]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } catch (err) {
      console.error('Error refreshing invoice templates:', err);
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const handleAddInvoiceTemplate = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCreateTemplate = useCallback(async () => {
    if (!templateName.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await apis.postInvoiceTemplate({
        invoice_template_name: templateName.trim(),
        template_description: templateDescription.trim() || '',
      });
      
      // Invalidate and refetch the query to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['invoiceTemplates'] });
      await queryClient.refetchQueries({ queryKey: ['invoiceTemplates'] });
      
      // Reset form and close dialog after successful creation and refresh
      setTemplateName('');
      setTemplateDescription('');
      setDialogOpen(false);
    } catch (error) {
      console.error('Error creating invoice template:', error);
      // Don't close dialog on error so user can retry
    } finally {
      setIsSubmitting(false);
    }
  }, [templateName, templateDescription, queryClient]);

  const handleModalState = (item) => {
    setActiveItem(item);
    setDeleteModalVisible(true);
  };

  const handleHideDeleteModal = () => {
    setDeleteModalVisible(false);
    setActiveItem({});
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'Date not available';
    try {
      const date = moment(dateString);
      if (!date.isValid()) return 'Invalid date';
      return date.format(globalDate);
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Date not available';
    }
  }, [globalDate]);

  const _renderItem = ({ item, index }) => {
    const parsedItem = parseInvoiceTemplate(item);

    return (
      <Pressable
        onPress={() => navigation.navigate('Invoice', { item: parsedItem })}
        style={{ flex: 1, maxWidth: "95%", minHeight: 200 }}
      >
        <Animatable.View
          useNativeDriver
          delay={index * 200}
          animation={'bounceIn'}
          style={[styles.noteContainer, { flex: 1, overflow: 'hidden', alignSelf: 'stretch', padding: 20, gap: 10, maxHeight: 400, flexDirection: 'column' }]}
        >
          <AchievementBackground
            green={parsedItem.type === 'project'}
            style={{ position: 'absolute', overflow: 'hidden', minWidth: '100%', opacity: 1, zIndex: -1, transform: [{ scale: isTablet ? 20 : 10 }] }}
          />
          <View style={{ flexDirection: 'row', alignContent: 'space-around', minWidth: '100%' }}>
            {/* Action Buttons */}
            <View style={{ position: 'absolute', top: -10, right: 10, flexDirection: 'row', gap: 8, zIndex: 10 }}>
              {/* Share Button */}
              <Pressable style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderRadius: 8, padding: 8 }}>
                <AntDesign name="share-alt" size={16} color="white" />
              </Pressable>

              {/* Delete Button */}
              <Pressable
                onPress={() => handleModalState(parsedItem)}
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.8)', borderRadius: 8, padding: 8 }}
              >
                <AntDesign name="delete" size={16} color="white" />
              </Pressable>
            </View>

            <View style={{ alignSelf: 'flex-start', flex: 10, gap: 10 }}>
              {/* Title */}
              <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 18 }} numberOfLines={1}>
                {parsedItem.title || 'Untitled Invoice Template'}
              </Text>

              {/* Created Date */}
              <Text style={{ color: "white", fontSize: 12 }}>
                Created: {formatDate(parsedItem.createdAt)}
              </Text>

              {/* Description */}
              {parsedItem.template_description ? (
                <View style={{ flex: 2, minHeight: 50 }}>
                  <Text style={{ fontSize: 10, color: 'white', opacity: 0.8 }}>Description</Text>
                  <Text style={{ color: 'white', fontSize: 12 }} numberOfLines={3}>
                    {parsedItem.template_description}
                  </Text>
                </View>
              ) : (
                <View style={{ flex: 2, minHeight: 50 }}>
                    <Text style={{ fontSize: 10, color: 'white', opacity: 0.6 }}>Description</Text>
                    <Text style={{ color: 'white', fontSize: 12, fontStyle: 'italic', opacity: 0.7 }}>
                      No description available
                    </Text>
                  </View>
              )}

              {/* Decorative Dollar Icon */}
              <MaskedView
                style={{ flex: 1, position: 'absolute', right: -20, bottom: -20, height: '100%', width: '100%', pointerEvents: 'none', overflow: "visible" }}
                maskElement={
                  <View style={{ flex: 1, position: 'absolute', bottom: -40, right: -20, overflow: 'visible' }}>
                    <FontAwesome name="dollar" size={24} color="black" />
                  </View>
                }
              >
                <LinearGradient
                  colors={['#ececec80', '#ececec20']}
                  start={{ x: 0.5, y: 0.9 }}
                  style={{ minHeight: '100%', minWidth: '100%', opacity: 0.8 }}
                />
              </MaskedView>

              {/* Creator Information */}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
                {parsedItem.creatorAvatar ? (
                  <AvatarHandler 
                    avatar={parsedItem.creatorAvatar}
                    avatarImageStyle={{ width: 30, height: 30, borderRadius: 15 }}
                    replacementImageStyle={{ width: 30, height: 30, borderRadius: 15 }}
                  />
                ) : (
                  <View style={{ width: 30, height: 30, borderRadius: 15, backgroundColor: 'rgba(255, 255, 255, 0.3)', justifyContent: 'center', alignItems: 'center' }}>
                    <AntDesign name="user" size={16} color="white" />
                  </View>
                )}
                <Text style={{ color: 'white', fontSize: 12 }} numberOfLines={1}>
                  {parsedItem.creatorName}
                </Text>
              </View>
            </View>
          </View>
        </Animatable.View>
      </Pressable>
    );
  };

  const renderEmptyState = () => (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
      <AntDesign name="file-text" size={64} color="rgba(255, 255, 255, 0.5)" />
      <Text style={{ color: 'white', fontSize: 18, marginTop: 20, textAlign: 'center' }}>
        {searchQuery ? 'No invoice templates found matching your search' : 'No invoice templates yet'}
      </Text>
      <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, marginTop: 10, textAlign: 'center' }}>
        {searchQuery ? 'Try a different search term' : 'Create your first invoice template to get started'}
      </Text>
    </View>
  );

  const renderErrorState = () => (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
      <AntDesign name="exclamation-circle" size={64} color="rgba(239, 68, 68, 0.8)" />
      <Text style={{ color: 'white', fontSize: 18, marginTop: 20, textAlign: 'center' }}>
        Failed to load invoice templates
      </Text>
      <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14, marginTop: 10, textAlign: 'center' }}>
        Please try again later
      </Text>
      <Pressable
        onPress={onRefresh}
        style={{ marginTop: 20, backgroundColor: 'rgba(255, 255, 255, 0.2)', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 }}
      >
        <Text style={{ color: 'white' }}>Retry</Text>
      </Pressable>
    </View>
  );

  const handleSearchChange = useCallback((text: string) => {
    setSearchQuery(text);
  }, []);

  return (
    <DefaultScreen
      containerStyle={{ zIndex: -1 }}
      scrollDisabled
      backButton
      searchBar={{
        placeHolder: 'Search Invoice Templates',
        searchState: searchQuery,
        setSearchState: setSearchQuery,
        callback: handleSearchChange,
        customIcons: [{
          function: () => handleAddInvoiceTemplate(),
          functionName: 'createInvoiceTemplate',
          icon: <AntDesign name="file-add" size={14} color="white" />
        }]
      }}
    >
      {/* <DeleteConfirmModal 
        itemId={activeItem.id} 
        deleteFunction={apis.deleteInvoiceTemplate} 
        title={activeItem?.title || "Invoice Template"} 
        refreshFunction={onRefresh} 
        setState={handleHideDeleteModal} 
        visibleState={deleteModalVisible} 
      /> */}
      
      {/* Add Invoice Template Dialog */}
      <Dialog key="addInvoiceTemplate" open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.6}
            style={{
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(8px)',
              })
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              {
                padding: 0,
                minWidth: 500,
                maxWidth: 600,
                maxHeight: "85%",
                borderRadius: 24,
                overflow: 'hidden',
                elevation: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.15,
                shadowRadius: 32,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              },
              Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
              }
            ]}
            opacity={1}
            y={0}
          >
            {/* Header */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <Feather
                    name="file-plus"
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack flex={1}>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    Add Invoice Template
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    Create a new invoice template for your team
                  </Dialog.Description>
                </YStack>
              </XStack>
            </View>

            {/* Content */}
            <View style={{ padding: 24 }}>
              <YStack gap="$4">
                {/* Template Name */}
                <YStack gap="$2">
                  <SizableText 
                    color={theme ? '#1f2937' : '#f9fafb'} 
                    fontWeight="600"
                    size="$4"
                  >
                    Template Name *
                  </SizableText>
                  <TextInput
                    placeholder="Enter template name..."
                    value={templateName}
                    onChangeText={setTemplateName}
                    style={{ 
                      fontSize: 16, 
                      padding: 12,
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 48,
                      fontWeight: '500',
                    }}
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                  />
                </YStack>

                {/* Template Description */}
                <YStack gap="$2">
                  <SizableText 
                    color={theme ? '#1f2937' : '#f9fafb'} 
                    fontWeight="600"
                    size="$4"
                  >
                    Description
                  </SizableText>
                  <TextInput
                    placeholder="Enter template description (optional)..."
                    value={templateDescription}
                    onChangeText={setTemplateDescription}
                    multiline
                    numberOfLines={4}
                    style={{ 
                      fontSize: 16, 
                      padding: 12,
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 100,
                      textAlignVertical: 'top',
                      fontWeight: '500',
                    }}
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                  />
                </YStack>
              </YStack>
            </View>

            {/* Footer Actions */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderTopColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack gap="$3" justifyContent="flex-end">
                <Dialog.Close asChild>
                  <Pressable
                    onPress={() => {
                      setTemplateName('');
                      setTemplateDescription('');
                      setDialogOpen(false);
                    }}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#6b7280',
                      minWidth: 100,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{
                      color: theme ? '#64748b' : '#d1d5db',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Cancel
                    </Text>
                  </Pressable>
                </Dialog.Close>
                <Pressable
                  onPress={handleCreateTemplate}
                  disabled={isSubmitting || !templateName.trim()}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: (isSubmitting || !templateName.trim()) 
                      ? (theme ? '#cbd5e1' : '#4b5563')
                      : (theme ? '#3b82f6' : '#2563eb'),
                    minWidth: 100,
                    alignItems: 'center',
                    opacity: (isSubmitting || !templateName.trim()) ? 0.6 : 1,
                    elevation: 2,
                    shadowColor: '#3b82f6',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                  }}
                >
                  {isSubmitting ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Create Template
                    </Text>
                  )}
                </Pressable>
              </XStack>
            </View>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
      <View style={{ zIndex: -1, flex: 1, padding: 20, flexDirection: 'column', width: '105%' }}>
        {isLoading ? (
          <View style={{ flex: 1, justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
            <ActivityIndicator size="large" color="white" />
            <Text style={{ color: 'white', marginTop: 10 }}>Loading invoice templates...</Text>
          </View>
        ) : error ? (
          renderErrorState()
        ) : filteredTemplates.length === 0 ? (
          renderEmptyState()
        ) : (
          <FlatList
            data={filteredTemplates}
            contentContainerStyle={{ gap: 20 }}
            renderItem={_renderItem}
            keyExtractor={(item, index) => item.id || item._id || `invoice-template-${index}`}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                tintColor="white"
              />
            } 
            ListEmptyComponent={renderEmptyState}
          />
        )}
      </View>
    </DefaultScreen>
  );
};

export default InvoiceTemplates;